<template>
  <div class="products-card-container" @click="trackProductClicked(product)">
    <ProductCard
      v-bind="productData"
      :ad="{ ...product.ad, optionTypeText }"
      :add-to-bag-text="addToBagText"
      :add-to-waitlist-text="$t('dictionary.waitlistMe')"
      :oos-text="$t('dictionary.oos')"
      :pdp-url="pdpUrl"
      :waitlisted-text="$t('dictionary.waitlisted')"
      :waitlisted-variant-ids="waitlistedVariantIds"
      :wishlisted-variant-ids="wishlistedVariantIds"
      :is-logged-in="userLoggedIn"
      :data-position="productIndex + 1"
      :data-list="entityType"
      :early-access-badge="earlyAccessBadge"
      :show-atb="!showRemindMe"
      :show-remind-me="showRemindMe"
      :is-cta-disabled="isCtaDisabled"
      :remind-me-text="remindMeText"
      :notification-subscribed-variant-ids="subscribedVariantIds"
      @add-to-wishlist="addToWishlist"
      @add-to-bag="addToBag"
      @add-to-waitlist="addToWaitlist"
      @remind-me="remindMe"
    >
      <template #default="slotProps">
        <SmartLink
          :native="false"
          :to="slotProps.pdpUrl"
          :aria-label="slotProps.pdpUrl"
          class="product-card-image-link"
        >
          <nuxt-img
            class="product-card-image"
            loading="lazy"
            :src="
              slotProps.featuredVariant.imageUrl ||
              slotProps.placeholderVariantImage
            "
            :alt="slotProps.alt"
          />
        </SmartLink>
      </template>
    </ProductCard>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ProductCard from '@sephora-asia/core-product-card/ProductCard.vue'
import i18n from '../mixins/i18n'
import {
  LaunchStatus,
  LAUNCH_CONFIG_PLATFORM
} from '../constants/productLaunchConstants'

export default {
  name: 'ProductCardContainer',
  components: {
    ProductCard
  },
  mixins: [i18n],
  props: {
    /**
     * Page entity type
     */
    entityType: {
      type: String,
      required: true
    },
    /**
     * Product attributes
     */
    product: {
      type: Object,
      required: true
    },
    /**
     * Index number for product
     */
    productIndex: {
      type: Number,
      required: true
    },
    /**
     * Carousel name for tracking
     */
    carouselName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isAddingToBag: false
    }
  },
  computed: {
    ...mapGetters({
      showVariantTagsOnPlp: 'globalConfig/showVariantTagsOnPlp',
      lineItems: 'cart/lineItems',
      waitlistedVariantIds: 'waitlist/waitlistedVariantIds',
      wishlistedVariantIds: 'wishlist/wishlistedVariantIds',
      userLoggedIn: 'user/loggedIn',
      userCardNumber: 'user/cardNumber',
      userId: 'user/id',
      userLoyaltyTier: 'user/loyaltyTier',
      subscribedVariantIds: 'earlyAccessNotification/subscribedVariantIds'
    }),

    productData() {
      const { featuredVariant } = this.product
      let label = ''

      if (this.showVariantTagsOnPlp) {
        const tags =
          featuredVariant.tags?.[this.$i18n.locale] || featuredVariant.tags
        let labels = []

        if (Array.isArray(tags)) {
          labels = tags.map((tag) => tag.text)
        }
        label = labels.join(' | ')
      } else if (this.product.isNew) {
        label = this.$t('dictionary.new')
      }

      const isProductSoldOut =
        this.product.soldOut &&
        !this.isComingSoonProduct(this.product.launchStatus)

      const isFeaturedVariantSoldOut =
        featuredVariant.soldOut &&
        !this.isComingSoonProduct(featuredVariant.launchStatus)

      return {
        ...this.product,
        label,
        soldOut: isProductSoldOut,
        featuredVariant: {
          ...featuredVariant,
          soldOut: isFeaturedVariantSoldOut
        }
      }
    },

    pdpUrl() {
      return (
        this.product.featuredVariant.url ||
        `${this.product.pdpUrl}/v/${
          this.product.featuredVariant.slugUrl || 'default'
        }`
      )
    },

    addToBagText() {
      if (this.isAddingToBag) {
        return this.$t('dictionary.addingToBag')
      } else {
        return this.$t('dictionary.addToBag')
      }
    },

    optionTypeText() {
      // Potion API response does not have optionType field atm
      return this.product.optionType
        ? this.$t(`products.ad.${this.product.optionType}Only`)
        : ''
    },

    carouselTrackingParams() {
      if (this.carouselName) {
        const entitySlug =
          this.entityType === 'brand'
            ? this.$route.params.id
            : this.entityType === 'category'
            ? this.$route.path.replace('/categories/', '')
            : ''

        return {
          origin: `${this.entityType}_${entitySlug}${
            this.product.strategy ? `|${this.product.strategy}` : ''
          }`,
          recommendation_id: this.product.recommendationId,
          carousel_name: this.carouselName,
          card_number: this.userCardNumber,
          session_id: this.$cookies.get('_ga_6KSFTBFXP6')?.split('.')[2],
          user_id: this.userId,
          site_country: this.$i18n.countryCode.toLowerCase(),
          traffic_split_key: this.product.splitKey
        }
      }
      return {}
    },

    earlyAccessBadge() {
      const { launchStatus } = this.product.featuredVariant
      if (launchStatus === LaunchStatus.Preview) {
        return this.$t('products.earlyAccess.comingSoon')
      }

      if (launchStatus === LaunchStatus.Prelaunch) {
        return this.isEarlyAccessQualifiedUser()
          ? this.$t('products.earlyAccess.priorityAccess')
          : this.$t('products.earlyAccess.comingSoon')
      }

      return ''
    },

    isValidPlatform() {
      const { launchConfig } = this.product.featuredVariant
      return launchConfig?.prelaunch?.platforms?.some((platform) =>
        LAUNCH_CONFIG_PLATFORM.includes(platform)
      )
    },

    isAppExclusive() {
      if (
        this.product.featuredVariant.launchStatus === LaunchStatus.Prelaunch
      ) {
        return !this.isValidPlatform && this.isEarlyAccessQualifiedUser()
      }
      return false
    },

    remindMeText() {
      return this.isAppExclusive
        ? this.$t('dictionary.appExclusive')
        : this.$t('dictionary.remindMe')
    },

    showRemindMe() {
      const { launchStatus, soldOut } = this.product.featuredVariant

      if (soldOut && this.isAppExclusive) return false

      if (launchStatus === LaunchStatus.Preview) return true

      return launchStatus === LaunchStatus.Prelaunch
        ? !(this.isEarlyAccessQualifiedUser() && this.isValidPlatform)
        : false
    },

    isCtaDisabled() {
      return (
        this.isAddingToBag ||
        (this.isAppExclusive && !this.product.featuredVariant.soldOut)
      )
    }
  },
  methods: {
    extractErrorMessage(error) {
      const errorMessage =
        error.error?.response?.data?.errors?.[0]?.meta?.message ||
        error.error?.message ||
        error.toString()
      return errorMessage
    },
    async addToBag() {
      this.isAddingToBag = true
      try {
        await this.$store.dispatch(
          'cart/createLineItem',
          this.product.featuredVariant.id
        )
        const lineItem = this.lineItems.find(
          (lineItem) => lineItem.variantId === this.product.featuredVariant.id
        )
        this._trackAddItemEvent(lineItem)
        this._openBag()
      } catch (error) {
        this.$notify({
          text: this.extractErrorMessage(error),
          type: 'error'
        })
      } finally {
        this.isAddingToBag = false
      }
    },
    _trackAddItemEvent(lineItem) {
      const data = {
        product_id: lineItem.productId,
        product_name: lineItem.productName,
        variant_id: lineItem.variantId,
        brand: lineItem.brandName,
        price: lineItem.unitPrice,
        quantity: lineItem.quantityValue,
        sku: lineItem.sapSku,
        ...this.carouselTrackingParams
      }
      this.$analytics.trackEvent(
        `${this.carouselName ? 'add_to_cart' : 'Add to Cart'}`,
        data
      )
    },
    _openBag() {
      this.$nuxt.$emit('open-cart-dropdown')
    },
    async addToWishlist(wishlistHash) {
      const { variantId } = wishlistHash

      if (!this.userLoggedIn) {
        return this._redirectGuest('wishlistId', variantId)
      }

      try {
        await this.$store.dispatch('wishlist/addToWishlist', variantId)
        this.$notify({
          text: this.$t('wishlist.added', {
            productName: this.product.productName
          })
        })
      } catch (error) {
        this.$notify({
          text: error,
          type: 'error'
        })
      }
    },
    async addToWaitlist(waitlistHash) {
      const { variantId } = waitlistHash

      if (!this.userLoggedIn) {
        return this._redirectGuest('waitlistId', variantId)
      }

      try {
        await this.$store.dispatch('waitlist/addToWaitlist', variantId)
      } catch (error) {
        this.$notify({
          text: error,
          type: 'error'
        })
      }
    },
    _redirectGuest(type, variantId) {
      let operator = '?'

      if (Object.keys(this.$router.currentRoute.query).length) {
        operator = '&'
      }

      const returnToURI = encodeURIComponent(
        `${this.$router.currentRoute.fullPath}${operator}${type}=${variantId}`
      )
      globalThis.location.href = `/sign_in?return-to=${returnToURI}`
    },
    trackProductClicked(product) {
      let searchTrackingData = {}
      if (this.entityType === 'search') {
        const searchPosition = [this.productIndex + 1]
        const pageFilters = ['page', 'sort', 'view']
        pageFilters.forEach((key) => {
          if (this.$route.query[key]) searchPosition.push(key)
        })
        if (
          this.$route.query.brands ||
          this.$route.query['min-price'] ||
          this.$route.query['max-price']
        ) {
          searchPosition.push('filter')
        }
        searchTrackingData = {
          search_position: searchPosition.join(' | '),
          search_term: this.$route.query.q
        }
      }

      this.$analytics.trackEvent('product_click', {
        product_id: product.id,
        variant_id: product.featuredVariant.id,
        product_tag: product.label,
        product_highlight: '',
        product_seal: product.featuredVariant.seal,
        origin: this.$nuxt?.context?.from?.path || document.referrer || '',
        card_sequence_num: this.productIndex + 1,
        status: product.featuredVariant.launchStatus,
        filter_type: this.earlyAccessBadge,
        ...searchTrackingData,
        ...this.carouselTrackingParams
      })
    },
    isComingSoonProduct(launchStatus) {
      return (
        launchStatus === LaunchStatus.Preview ||
        (launchStatus === LaunchStatus.Prelaunch &&
          !this.isEarlyAccessQualifiedUser())
      )
    },
    isEarlyAccessQualifiedUser() {
      return (
        this.userLoggedIn &&
        this.userLoyaltyTier &&
        this.product?.featuredVariant?.launchConfig?.prelaunch?.tiers?.includes(
          this.userLoyaltyTier
        )
      )
    },
    async remindMe(remindMeHash) {
      const { variantId } = remindMeHash
      try {
        if (!this.userLoggedIn) {
          return this._redirectGuest('subscribedVariantId', variantId)
        }

        // Qualified user will subscribe for prelaunch and Non-Qualified user will subscribe for product official launch
        const type = this.isEarlyAccessQualifiedUser() ? 'prelaunch' : 'launch'

        await this.$store.dispatch(
          'earlyAccessNotification/subscribeToNotification',
          {
            eventType: `products_${type}`,
            eventName:
              this.product?.featuredVariant?.launchConfig[type]?.name || '',
            variantId: this.product?.featuredVariant.id
          }
        )
        this.$notify({
          text: this.$t('products.earlyAccess.successfullyNotified')
        })
      } catch (error) {
        this.$notify({
          text: this.extractErrorMessage(error),
          type: 'error'
        })
      }
    }
  }
}
</script>
